import { useState, useCallback, useEffect, useMemo } from "react";
import { Space, Popconfirm, Button, Table, message, Select, Image, Form, Tag, Input, Card, Row, Col, Typography } from "antd";
import { DeleteOutlined, EyeOutlined, FilePdfFilled, DownloadOutlined, MessageOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";


import {
  getWorklogs,
  getWorklog,
  deleteWorklog,
  updateWorklog,
  createWorklog,
  printWorklog,
  exportWorklogsToExcel,
  sendSms,
} from "../../api/worklog";
import { getFileUrl } from "../../api/file";
import { getProject, getProjects } from "../../api/project";
import { getTask, getTasks } from "../../api/task";
import { getUsers } from "../../api/user";
import { UserRole, WorklogStatus } from "../../lib/constants";

import PageHeader from "../../components/shared/PageHeader";
import { EntityForm } from "../../components/EntityForm";
import BlueButton from "../../components/shared/BlueButton";
import DateRangeFilter from "../../components/shared/DateRangeFilter";
import { PER_PAGE } from "../../configs";
import { useSearchParams } from "react-router-dom";
import { getEquipmentAccessories } from "../../api/equipmentAccessories";

dayjs.extend(utc);

export default function WorkLogsView({ user }) {
  const perPage = PER_PAGE;
  const [searchParams, setSearchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [worklogs, setWorklogs] = useState([]);
  const [showCreateWorklog, setShowCreateWorklog] = useState(false);
  const [editWorklog, setEditWorklog] = useState(null);
  const [searchProjects, setSearchProjects] = useState([]);
  const [searchTasks, setSearchTasks] = useState([]);
  const [searchUsers, setSearchUsers] = useState([]);

  const [page, setPage] = useState(
    searchParams.get("page") ? parseInt(searchParams.get("page")) : 1
  );
  const [searchProjectId, setSearchProjectId] = useState(
    searchParams.get("project") ? parseInt(searchParams.get("project")) : null
  );
  const [project, setProject] = useState(
    searchParams.get("projectId") ? parseInt(searchParams.get("projectId")) : null
  );
  const [task, setTask] = useState(
    searchParams.get("taskId") ? parseInt(searchParams.get("taskId")) : null
  );
  const [userFilter, setUserFilter] = useState(
    searchParams.get("users") ? searchParams.get("users").split(",").map(id => parseInt(id)) : null
  );
  const [range, setRange] = useState(
    searchParams.get("startDate") && searchParams.get("endDate")
      ? {
        startDate: searchParams.get("startDate"),
        endDate: searchParams.get("endDate")
      }
      : null
  );

  const dataIndex = (page - 1) * perPage;

  // State for accessories
  const [accessories, setAccessories] = useState([]);
  const [accessoryValues, setAccessoryValues] = useState({});

  const [form] = Form.useForm();

  const updateUrlParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        newParams.delete(key);
      } else if (Array.isArray(value)) {
        if (value.length > 0) {
          newParams.set(key, value.join(','));
        } else {
          newParams.delete(key);
        }
      } else {
        newParams.set(key, value.toString());
      }
    });

    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const currentPage = searchParams.get("page") ? parseInt(searchParams.get("page")) : 1;
    const currentProject = searchParams.get("project") ? parseInt(searchParams.get("project")) : null;
    const currentUsers = searchParams.get("users") ? searchParams.get("users").split(",").map(id => parseInt(id)) : null;
    const currentStartDate = searchParams.get("startDate") || null;
    const currentEndDate = searchParams.get("endDate") || null;
    const currentTask = searchParams.get("taskId") ? parseInt(searchParams.get("taskId")) : null;

    setPage(currentPage);
    setSearchProjectId(currentProject);
    setUserFilter(currentUsers);
    setRange(currentStartDate && currentEndDate ? { startDate: currentStartDate, endDate: currentEndDate } : null);
    setTask(currentTask);

    if (currentUsers) {
      form.setFieldValue("users", currentUsers);
    } else {
      form.setFieldValue("users", []);
    }

    fetchWorklogs({
      page: currentPage,
      perPage,
      projectId: currentProject,
      taskId: currentTask,
      startAt: currentStartDate,
      endAt: currentEndDate,
      users: currentUsers,
    });
  }, [searchParams]);

  useEffect(() => {
    // For create worklog given projectId and taskId
    if (searchParams.get("create") === "True" && searchParams.get("projectId") && searchParams.get("taskId")) {
      setShowCreateWorklog(true)
    }
  }, [searchParams]);

  useEffect(() => {
    // Handle viewing a specific worklog by ID
    const worklogId = searchParams.get("id");
    if (worklogId) {
      const fetchWorklogById = async () => {
        try {
          setIsLoading(true);
          const worklog = await getWorklog(parseInt(worklogId));
          setEditWorklog(worklog);
        } catch (error) {
          console.error("Error fetching worklog:", error);
          message.error("Failed to load worklog");
        } finally {
          setIsLoading(false);
        }
      };
      fetchWorklogById();
    }
  }, [searchParams.get("id")]);

  const populateSearch = useCallback(async () => {
    if (editWorklog) {
      const p = await getProject(editWorklog.project.id);
      setSearchProjects([p]);
      const { results: t } = await getTasks({ projectId: editWorklog.project.id });
      setSearchTasks(t);
    } else {
      const projects = await getProjects({ perPage: 100, page: 1 });
      const tasks = await getTasks({ projectId: project });
      setSearchTasks(tasks.results);
      setSearchProjects(projects.results);
    }
  }, [project, editWorklog]);

  useEffect(() => {
    populateSearch();
  }, [populateSearch, project]);


  useEffect(() => {
    // If we are in edit mode, use the accessories from the worklog table
    if (editWorklog && editWorklog.accessories) {
      const accessories = Object.keys(editWorklog.accessories);
      const accessoryValues = editWorklog.accessories

      setAccessories(accessories);
      setAccessoryValues(accessoryValues);
    }
  }, [editWorklog])

  // Fetch task details and check for equipment accessories when task changes
  useEffect(() => {
    if (!task && !editWorklog) {
      setAccessories([]);
      setAccessoryValues({});
      return;
    }
    const fetchTaskDetails = async () => {
      try {
        if (!editWorklog) {
          // For new worklog creation
          const taskDetails = await getTask(task);
          if (taskDetails.equipment && taskDetails.equipment.id) {
            // Get equipment accessories
            const { results } = await getEquipmentAccessories({ equipmentId: taskDetails.equipment.id });

            if (!results || results.length === 0) {
              setAccessories([]);
              return;
            }

            const accessoryList = results[0].accessories;
            setAccessories(accessoryList);

            // Initialize with empty values for new accessories
            const initialValues = {};
            accessoryList.forEach(accessory => {
              initialValues[accessory] = '';
            });
            setAccessoryValues(initialValues);
          }
        }
      } catch (error) {
        console.error("Error fetching task details:", error);
      }
    };

    fetchTaskDetails();
  }, [task, editWorklog]);

  const projectSearchOptions = useMemo(() => {
    return searchProjects.map((project) => {
      return {
        label: project.name,
        value: project.id,
      };
    });
  }, [searchProjects]);

  const taskSearchOptions = useMemo(() => {
    return searchTasks.map((task) => {
      return {
        label: task.name,
        value: task.id,
      };
    });
  }, [searchTasks]);

  const userSearchOptions = useMemo(() => {
    return searchUsers.map((user) => {
      return {
        label: user.firstName + " " + (user.lastName || ""),
        value: user.id,
      };
    })
  }, [searchUsers]);

  const handleUpdateAccessoryValue = (accessory, value) => {
    setAccessoryValues(prev => ({
      ...prev,
      [accessory]: value
    }));
  };

  const AccessoriesSection = useMemo(() => {
    if (!accessories || accessories.length === 0) {
      return null;
    }

    const accessoryRows = [];
    for (let i = 0; i < accessories.length; i += 2) {
      accessoryRows.push(accessories.slice(i, i + 2));
    }

    return (
      <Card
        title="Equipment Accessories"
        className="mb-6"
        bordered={false}
        style={{ marginBottom: '16px' }}
      >
        {accessories.length === 0 ? (
          <Typography.Text type="secondary">No accessories found for the selected equipment</Typography.Text>
        ) : (
          <>
            {accessoryRows.map((row, rowIndex) => (
              <Row key={rowIndex} gutter={16} style={{ marginBottom: '12px' }}>
                {row.map((accessory) => (
                  <Col span={12} key={accessory}>
                    <Form.Item
                      label={accessory}
                      style={{ marginBottom: '8px' }}
                    >
                      <Input
                        value={accessoryValues[accessory] || ''}
                        onChange={!editWorklog ? (e) => handleUpdateAccessoryValue(accessory, e.target.value) : undefined}
                        disabled={!!editWorklog} // Disable input in view/edit mode
                      />
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            ))}
          </>
        )}
      </Card>
    );
  }, [accessories, accessoryValues, editWorklog]);

  const sections = useMemo(() => {
    const signatures = [];
    if (editWorklog && editWorklog.signature) {
      signatures.push({
        ...editWorklog.signature,
        uid: editWorklog.signature.id,
        name: editWorklog.signature.originalName,
        status: "done",
        url: getFileUrl(editWorklog.signature),
        type: null,
      });
    }

    const formItems = [
      {
        items: [
          {
            type: "avatar",
            label: "Signature (2MB)",
            name: "signature",
            col: 12,
            maxLen: 200,
            fileList: signatures,
            required: false,
          },

          {
            type: "text",
            label: "Signature Name",
            name: "signatureName",
            col: 12,
            maxLen: 200,
            required: false,
          },
        ],
      },
    ];

    // Only show site images upload field if not in view mode (since they're displayed at the bottom)
    if (!editWorklog) {
      formItems.push({
        items: [
          {
            type: "attachments",
            label: "Site Images (2MB)",
            name: "siteImages",
            col: 12,
            maxLen: 200,
            fileList: editWorklog?.siteImages?.map(image => ({
              ...image,
              uid: image.id,
              name: image.originalName,
              status: 'done',
              url: getFileUrl(image),
              type: null,
            })) || [],
            required: false,
            multiple: true,
          },
        ],
      });
    }

    formItems.push({
      items: [
        {
          type: "async-select",
          name: "projectId",
          label: "Project",
          placeholder: "Select Project",
          col: 12,
          required: true,
          options: projectSearchOptions,
          onSearch: async (q) => {
            const { results: projects } = await getProjects({ q });
            setSearchProjects(projects);
          },
          onSelect: (value) => {
            if (value) {
              setProject(value);
            }
          },
        },
        {
          type: "async-select",
          placeholder: "Select Project Task",
          name: "taskId",
          label: "Task",
          col: 12,
          disabled: !project,
          options: taskSearchOptions,
          onSearch: async (q) => {
            const { results: tasks } = await getTasks({
              q,
              projectId: project,
            });
            setSearchTasks(tasks);
          },
          onSelect: (value) => {
            if (value) {
              setTask(value);
            }
          },
        },
      ],
    });

    formItems.push({
      items: [
        {
          type: "date",
          label: "Start Date",
          name: "startAt",
          showTime: true,
          format: "YYYY-MM-DD HH:mm a",
          required: true,
          col: 12,
        },
        {
          type: "date",
          label: "End Date",
          name: "endAt",
          showTime: true,
          format: "YYYY-MM-DD HH:mm a",
          required: true,
          col: 12,
        },
      ],
    });

    formItems.push({
      items: [
        {
          type: "time",
          label: "Work Duration",
          name: "workDuration",
          format: "HH:mm:ss",
          required: true,
          col: 12,
        },
        {
          type: "time",
          label: "Break Duration",
          name: "breakDuration",
          format: "HH:mm:ss",
          required: true,
          col: 12,
        },
      ],
    });

    if ([UserRole.Admin, UserRole.User].includes(user.role)) {
      formItems.push(
        ...[
          {
            items: [
              {
                type: "async-select",
                label: "Status",
                name: "status",
                required: true,
                col: 12,
                options: Object.keys(WorklogStatus).map((status) => {
                  return {
                    value: WorklogStatus[status],
                    label: status,
                  };
                }),
              },
              {
                type: "text-area",
                label: "Reason",
                name: "reason",
                col: 12,
                maxLen: 200,
                required: false,
                disabled: true
              }
            ],
          },
        ]
      );
    }

    return formItems;
  }, [
    editWorklog,
    project,
    projectSearchOptions,
    taskSearchOptions,
    user.role,
  ]);

  const fetchWorklogs = useCallback(async ({ page, perPage, projectId, taskId, sortBy, sortType, startAt, endAt, users }) => {
    try {
      setIsLoading(true);
      const { total, results } = await getWorklogs({
        page,
        perPage,
        projectId,
        taskId,
        sortBy,
        sortType,
        startAt,
        endAt,
        users,
      });
      setTotal(total);
      setWorklogs(results);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  }, []);

  const handleExportToExcel = async () => {
    try {
      setIsLoading(true);
      await exportWorklogsToExcel({
        projectId: searchProjectId,
        taskId: task,
        startAt: range?.startDate,
        endAt: range?.endDate,
        users: userFilter,
      });
      message.success("Worklogs exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export worklogs");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async ({ ...worklogData }) => {
    if (worklogData.startAt > worklogData.endAt) {
      message.error("Start date must be less than end date");
      throw new Error("Start date must be less than end date");
    }

    const signature =
      worklogData.signature &&
        worklogData.signature.fileList &&
        worklogData.signature.fileList.length
        ? worklogData.signature.fileList.map((file) => {
          if (!file.response) return file;
          return file.response.file;
        })[0]
        : worklogData.signature;

    const siteImages =
      worklogData.siteImages &&
        worklogData.siteImages.fileList &&
        worklogData.siteImages.fileList.length
        ? worklogData.siteImages.fileList.map((file) => {
          if (!file.response) return file;
          return file.response.file;
        })
        : worklogData.siteImages;

    worklogData.workDuration = dayjs(worklogData.workDuration).format(
      "HH:mm:ss"
    );
    worklogData.breakDuration = dayjs(worklogData.breakDuration).format(
      "HH:mm:ss"
    );

    if (editWorklog) {
      let sendSms = false;
      if (editWorklog.status === worklogData.status) {
        sendSms = true;
      }

      await updateWorklog({
        ...worklogData,
        signature,
        siteImages,
        id: editWorklog.id,
        sendSms: false // Do not send SMS automatically, it'll be handled by a button click
      });
    } else {
      await createWorklog({
        ...worklogData,
        signature,
        siteImages,
        accessories: JSON.stringify(accessoryValues),
        sendSms: false // Do not send SMS automatically, it'll be handled by a button click
      });
    }
    setEditWorklog(null);
    setProject(null);
    setShowCreateWorklog(false);

    await fetchWorklogs({
      page,
      perPage,
      projectId: searchProjectId,
      taskId: task,
      startAt: range?.startDate,
      endAt: range?.endDate,
      users: userFilter,
    });
  };

  const handleDelete = async (worklog) => {
    await deleteWorklog(worklog.id);

    await fetchWorklogs({
      page,
      perPage,
      projectId: searchProjectId,
      taskId: task,
      startAt: range?.startDate,
      endAt: range?.endDate,
      users: userFilter,
    });
  };

  const handlePrint = async (worklog) => {
    setIsLoading(true);
    await printWorklog(worklog.id)
      .then(async (_) => {
        await updateWorklog({
          ...worklog,
          id: worklog.id,
          isDownloaded: true,
          projectId: worklog.project.id,
          taskId: worklog.task.id,
        });
      })
      .catch((err) => message.error("Error loading pdf"))
      .finally(() => setIsLoading(false));
  };

  const handleProjectFilter = (projectId) => {
    updateUrlParams({
      project: projectId,
      page: 1 // Reset to first page when filtering
    });
  };

  const handleDateRangeChange = ({ startDate, endDate }) => {
    const parsedStartDate = Date.parse(startDate);
    const parsedEndDate = Date.parse(endDate);

    if (!isNaN(parsedStartDate) && !isNaN(parsedEndDate)) {
      updateUrlParams({
        startDate,
        endDate,
        page: 1 // Reset to first page when filtering
      });
    }

    const users = form.getFieldValue("users");
    if (users && users.length > 0) {
      updateUrlParams({
        startDate,
        endDate,
        users,
        page: 1
      });
    }
  };

  const handleDateRangeClear = () => {
    updateUrlParams({
      startDate: null,
      endDate: null,
      users: null,
      page: 1
    });
    form.setFieldValue("users", []);
  };

  const handleUserFilterChange = (users) => {
    updateUrlParams({
      users: users && users.length > 0 ? users : null,
      page: 1 // Reset to first page when filtering
    });
  };

  const handlePaginationChange = (page) => {
    updateUrlParams({ page });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const updates = {
      page: pagination.current,
    };

    if (sorter.field) {
      updates.sortBy = sorter.field;
      updates.sortOrder = sorter.order === "ascend" ? "asc" : "desc";
    } else {
      updates.sortBy = null;
      updates.sortOrder = null;
    }

    updateUrlParams(updates);
  };

  const statusColors = {
    verified: 'green',
    pending: 'gold',
    rejected: 'red'
  };

  const TableCols = [
    { title: "No", dataIndex: "no", key: "no", width: 80 },
    { title: "Project", dataIndex: ["project", "name"], key: "project" },
    {
      title: "User",
      dataIndex: ["worklogUser", "firstName"],
      key: "worklogUser",
    },
    { title: "Task", dataIndex: ["task", "name"], key: "task" },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'statusTag',
      render: (status) => (
        <Tag color={statusColors[status.toLowerCase()]}>
          {status}
        </Tag>
      )
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      sorter: true,
      render: (text) => dayjs(text).format("DD MMM YYYY HH:mm A"),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record, index) => (
        <Space size={2}>
          <Popconfirm
            title="Delete the task"
            description="Are you sure to delete this worklog?"
            onConfirm={() => handleDelete(worklogs[index])}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" icon={<DeleteOutlined />} danger></Button>
          </Popconfirm>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => setEditWorklog(worklogs[index])}
          />

          <Button
            icon={<FilePdfFilled />}
            onClick={() => handlePrint(worklogs[index])}
          >
            Download Pdf
          </Button>

          <Button
            icon={<MessageOutlined />}
            onClick={async () => {
              try {
                setIsLoading(true);
                await sendSms(worklogs[index].id);
                message.success("SMS sent successfully!");
              } catch (error) {
                message.error("Failed to send SMS");
              } finally {
                setIsLoading(false);
              }
            }}
          >
            Send SMS
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <main className="min-h-full flex flex-col space-y-6">
      <PageHeader title="Worklogs" home="App" route="/" />

      <div className="flex-grow bg-white ml-6 p-6 flex flex-col space-y-6">
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Select
            placeholder="Search Project"
            value={searchProjectId}
            onSearch={async (str) => {
              const { results: projects } = await getProjects({ q: str });
              setSearchProjects(projects);
            }}
            options={searchProjects.map((project) => {
              return {
                value: project.id,
                label: project.name,
              };
            })}
            defaultActiveFirstOption={false}
            filterOption={false}
            onChange={handleProjectFilter}
            showSearch
            style={{ width: 200 }}
            allowClear
          />

          <div className="flex gap-2 items-center">
            <DateRangeFilter
              value={range}
              onChange={handleDateRangeChange}
              onClear={handleDateRangeClear}
            >
              <Form form={form} layout="horizontal">
                <Form.Item name="users" label="User">
                  <Select
                    className="col-span-2"
                    showSearch
                    placeholder="Search User"
                    mode="multiple"
                    value={userFilter}
                    onSearch={async (str) => {
                      const { results: users } = await getUsers({ q: str });
                      setSearchUsers(users);
                    }}
                    options={userSearchOptions}
                    defaultActiveFirstOption={false}
                    filterOption={false}
                    onChange={handleUserFilterChange}
                    allowClear
                  />
                </Form.Item>
              </Form>
            </DateRangeFilter>

            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportToExcel}
              loading={isLoading}
            >
              Download Excel
            </Button>

            <BlueButton
              type={"primary"}
              onClick={() => setShowCreateWorklog(true)}
            >
              Create Worklog
            </BlueButton>
          </div>
        </Space>

        <Table
          dataSource={worklogs.map((worklog, index) => {
            return {
              no: dataIndex + index + 1,
              ...worklog,
              startAt: dayjs.utc(worklog.startAt).local().format("YYYY-MM-DD HH:mm a"),
              endAt: dayjs.utc(worklog.endAt).local().format("YYYY-MM-DD HH:mm a"),
              status: Object.keys(WorklogStatus).find(
                (status) => WorklogStatus[status] === worklog.status
              ),
            };
          })}
          columns={TableCols}
          loading={isLoading}
          pagination={{
            size: "small",
            total,
            pageSize: perPage,
            current: page,
            onChange: handlePaginationChange,
            showQuickJumper: true,
            showTotal: () => `Total ${total} worklogs`,
            position: ["bottomLeft"],
          }}
          onChange={handleTableChange}
        />

        {(showCreateWorklog || editWorklog) && (
          <EntityForm
            initialValues={
              editWorklog
                ? {
                  ...editWorklog,
                  startAt: dayjs.utc(editWorklog.startAt).local(),
                  endAt: dayjs.utc(editWorklog.endAt).local(),
                  workDuration: dayjs(editWorklog.workDuration, "HH:mm:ss"),
                  breakDuration: dayjs(editWorklog.breakDuration, "HH:mm:ss"),
                  projectId: editWorklog.project.id,
                  taskId: editWorklog.task && editWorklog.task.id,
                }
                : (project && task) ? { projectId: project, taskId: task } : null
            }
            sections={sections}
            title="Worklog"
            open={showCreateWorklog || editWorklog}
            onCancel={() => {
              setShowCreateWorklog(false);
              setEditWorklog(null);
              setProject(null);
            }}
            onSubmit={handleSubmit}
            onDelete={() =>
              handleDelete(editWorklog).then(() => setEditWorklog(null))
            }
            isEditMode={!!editWorklog}
          >
            {AccessoriesSection}

            {
              editWorklog && editWorklog.siteImages && (editWorklog.siteImages.length > 0) && (
                <div className="grid grid-cols-2 gap-2">
                  <h3 className="col-span-2">Site Images</h3>
                  {editWorklog.siteImages.map(image => (
                    <Image key={image.id} src={getFileUrl(image)} alt="site image" height={200} className="object-cover" />
                  ))}
                </div>
              )
            }
          </EntityForm>
        )}
      </div>
    </main>
  );
}
